import { useEffect, useState } from 'react';
import { apiClient } from '@/services/api';
import type { SyncedMenuCategory } from '@/types/api';
import { ColorPickerField } from '@/components/ui/ColorPickerField';
import { TranslationsEditor } from '@/components/ui/TranslationsEditor';

interface MenuCategoryFormProps {
  formData: Partial<SyncedMenuCategory>;
  onChange: (data: Partial<SyncedMenuCategory>) => void;
  restaurantId: number;
}

export function MenuCategoryForm({ formData, onChange, restaurantId }: MenuCategoryFormProps) {
  // `posId` is null for a mgmt-created shift the POS hasn't acknowledged yet.
  // Falling back to the mgmt `id` here would silently write a management-server
  // PK into shiftIds, a POS-native reference field -- a different, real shift
  // could share that numeric id (F3).
  const [shifts, setShifts] = useState<{ id: number; posId: number | null; name: string }[]>([]);

  useEffect(() => {
    if (!restaurantId) return;
    apiClient.getSyncedShifts(restaurantId).then((data) => {
      setShifts(data.map((s: any) => ({ id: s.id, posId: s.posShiftId ?? null, name: s.name })));
    }).catch(() => setShifts([]));
  }, [restaurantId]);

  const handleShiftToggle = (posId: number) => {
    const current = formData.shiftIds || [];
    const updated = current.includes(posId)
      ? current.filter((id: number) => id !== posId)
      : [...current, posId];
    onChange({ ...formData, shiftIds: updated });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input
          type="text"
          value={formData.name || ''}
          onChange={(e) => onChange({ ...formData, name: e.target.value })}
          className="input w-full"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isVisible"
            checked={formData.isVisible ?? false}
            onChange={(e) => onChange({ ...formData, isVisible: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="isVisible" className="ml-2 text-sm text-gray-700">Visible</label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isBeverage"
            checked={formData.isBeverage ?? false}
            onChange={(e) => onChange({ ...formData, isBeverage: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="isBeverage" className="ml-2 text-sm text-gray-700">Beverage</label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isVisibleOnSite"
            checked={formData.isVisibleOnSite ?? false}
            onChange={(e) => onChange({ ...formData, isVisibleOnSite: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="isVisibleOnSite" className="ml-2 text-sm text-gray-700">Visible on Site</label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isShortcut"
            checked={formData.isShortcut ?? false}
            onChange={(e) => onChange({ ...formData, isShortcut: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="isShortcut" className="ml-2 text-sm text-gray-700">Shortcut</label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isMenuGroupShortcut"
            checked={formData.isMenuGroupShortcut ?? false}
            onChange={(e) => onChange({ ...formData, isMenuGroupShortcut: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="isMenuGroupShortcut" className="ml-2 text-sm text-gray-700">Menu Group Shortcut</label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
        <input
          type="number"
          value={formData.sortOrder ?? ''}
          onChange={(e) => onChange({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
          className="input w-full"
        />
      </div>

      {shifts.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Shifts</label>
          <div className="border rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
            {shifts.map((shift) => {
              const unsynced = shift.posId === null;
              const checked = shift.posId !== null && (formData.shiftIds || []).includes(shift.posId);
              return (
                <div key={shift.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`mc-shift-${shift.id}`}
                    checked={checked}
                    disabled={unsynced}
                    title={unsynced ? 'Not yet synced to the POS -- cannot be linked yet' : undefined}
                    onChange={() => shift.posId !== null && handleShiftToggle(shift.posId)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label
                    htmlFor={`mc-shift-${shift.id}`}
                    className={`ml-2 text-sm ${unsynced ? 'text-gray-400 italic' : 'text-gray-700'}`}
                  >
                    {shift.name}{unsynced ? ' (unsynced)' : ''}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ColorPickerField
        label="Button Color"
        value={formData.buttonColorCode}
        onChange={(v) => onChange({ ...formData, buttonColorCode: v })}
      />
      <ColorPickerField
        label="Text Color"
        value={formData.textColorCode}
        onChange={(v) => onChange({ ...formData, textColorCode: v })}
      />
      <TranslationsEditor
        restaurantId={restaurantId}
        translations={formData.translations || []}
        onChange={(translations) => onChange({ ...formData, translations })}
      />
    </div>
  );
}
